import React from 'react';
import { Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import logoIcon from '../../assets/concreto_tec_logo.png';
import { LogoRelatorio, AssinaturasRodape } from './PersonalizacaoRelatorio';

// Peças compartilhadas pelos PDFs de Medição (moldagem e ruptura): capa, cabeçalho, rodapé e estilos de tabela.

export const styles = StyleSheet.create({
  page: { fontFamily: 'Helvetica', fontSize: 8, padding: 24 },
  coverPage: { fontFamily: 'Helvetica', padding: 48 },
  coverBrandRow: { flexDirection: 'row', alignItems: 'center' },
  coverIcon: { width: 48, height: 48 },
  coverBrandText: { marginLeft: 14 },
  coverWordmark: { fontSize: 18, fontFamily: 'Helvetica-Bold' },
  coverTagline: { fontSize: 8, letterSpacing: 1, color: '#0e7490', marginTop: 2 },
  coverSpacer: { flex: 1, justifyContent: 'flex-end' },
  coverTitleBlock: { paddingTop: 40, borderTop: '1.5pt solid #000' },
  coverKicker: { fontSize: 9, letterSpacing: 1, color: '#666', marginBottom: 8 },
  coverTitle: { fontSize: 20, fontFamily: 'Helvetica-Bold', lineHeight: 1.25 },
  coverMetaGrid: { marginTop: 28 },
  coverMetaField: { marginBottom: 14 },
  coverMetaLabel: { fontSize: 7, letterSpacing: 0.5, color: '#888', marginBottom: 3 },
  coverMetaValue: { fontSize: 10, borderBottom: '0.75pt solid #ccc', paddingBottom: 5 },
  coverFooter: { paddingTop: 16, flexDirection: 'row', justifyContent: 'space-between', fontSize: 8, color: '#888' },
  headerBox: { border: '1pt solid #000', marginBottom: 6 },
  headerTop: { flexDirection: 'row', borderBottom: '1pt solid #000' },
  headerTitleBlock: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 6, borderRight: '1pt solid #000' },
  headerTitle: { fontSize: 11, fontFamily: 'Helvetica-Bold', textAlign: 'center' },
  headerLogoBlock: { width: 100, alignItems: 'center', justifyContent: 'center', padding: 6 },
  headerMeta: { flexDirection: 'row' },
  metaCell: { flex: 1, flexDirection: 'row', padding: '4 6', borderRight: '1pt solid #000', minWidth: 0 },
  metaCellLast: { flex: 1, flexDirection: 'row', padding: '4 6', minWidth: 0 },
  metaLabel: { fontFamily: 'Helvetica-Bold', marginRight: 4, flexShrink: 0 },
  metaValue: { flexShrink: 1 },
  sectionTitle: { fontSize: 10, fontFamily: 'Helvetica-Bold', marginTop: 8, marginBottom: 4 },
  summaryRow: { flexDirection: 'row', marginBottom: 6 },
  summaryBox: { border: '1pt solid #000', padding: '6 10', marginRight: 8, minWidth: 90, alignItems: 'center' },
  summaryValue: { fontSize: 14, fontFamily: 'Helvetica-Bold' },
  summaryLabel: { fontSize: 7, color: '#444', marginTop: 2, textAlign: 'center' },
  // Sem borda no container: cada linha desenha as próprias bordas, para não ficar incompleta na quebra de página.
  table: { marginTop: 2 },
  tableRow: { flexDirection: 'row', borderLeft: '1pt solid #000', borderRight: '1pt solid #000', borderBottom: '1pt solid #000' },
  tableRowFirst: { flexDirection: 'row', border: '1pt solid #000' },
  tableRowEven: { backgroundColor: '#f5f5f5' },
  th: { fontFamily: 'Helvetica-Bold', padding: '4 4', borderRight: '1pt solid #000', textAlign: 'center' },
  thLast: { fontFamily: 'Helvetica-Bold', padding: '4 4', textAlign: 'center' },
  td: { padding: '4 4', borderRight: '1pt solid #000', textAlign: 'center' },
  tdLast: { padding: '4 4', textAlign: 'center' },
  weekend: { backgroundColor: '#fef3c7' },
  signatureFooter: { marginTop: 16 },
  footer: { marginTop: 8, flexDirection: 'row', justifyContent: 'space-between' },
  footerText: { fontSize: 7, color: '#666' },
});

// Exibe a data no padrão brasileiro (dd/mm/aaaa) para uso dentro do PDF.
export function formatDateBrPdf(value) {
  if (!value) return '-';
  const s = String(value);
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const p = new Date(s);
  if (Number.isNaN(p.getTime())) return s;
  return p.toLocaleDateString('pt-BR');
}

// Exibe um número com 2 casas decimais, ou '-' se ausente/inválido.
export function formatNumeroPdf(value) {
  if (value == null || !Number.isFinite(Number(value))) return '-';
  return Number(value).toFixed(2).replace('.', ',');
}

// Exibe inteiros/decimais sem zeros desnecessários (ex.: 28, 7, 2,5).
export function formatInteiroPdf(value) {
  if (value == null || value === '' || !Number.isFinite(Number(value))) return '-';
  return String(Number(value)).replace('.', ',');
}

export const DIAS_SEMANA = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

// Tabela simples: columns = [{ key, label, width, render? }]; faixas alternadas e cabeçalho repetido a cada página.
export function SimpleTable({ columns, rows, rowStyle }) {
  return (
    <View style={styles.table}>
      <View style={styles.tableRowFirst} fixed>
        {columns.map((col, i) => (
          <Text key={col.key} style={[i === columns.length - 1 ? styles.thLast : styles.th, { width: col.width }]}>
            {col.label}
          </Text>
        ))}
      </View>
      {rows.map((row, index) => (
        <View
          key={row.key ?? index}
          style={[styles.tableRow, index % 2 === 1 ? styles.tableRowEven : null, rowStyle ? rowStyle(row) : null]}
          wrap={false}
        >
          {columns.map((col, i) => (
            <Text key={col.key} style={[i === columns.length - 1 ? styles.tdLast : styles.td, { width: col.width }]}>
              {col.render ? col.render(row) : row[col.key] ?? '-'}
            </Text>
          ))}
        </View>
      ))}
    </View>
  );
}

// Capa A4 retrato.
export function MedicaoCapa({ kicker, titulo, empresa, obra, periodo }) {
  return (
    <Page size="A4" style={styles.coverPage}>
      <View style={styles.coverBrandRow}>
        <Image style={styles.coverIcon} src={logoIcon} />
        <View style={styles.coverBrandText}>
          <Text style={styles.coverWordmark}>concreto.tec.br</Text>
          <Text style={styles.coverTagline}>TECNOLOGIA EM CONCRETO</Text>
        </View>
      </View>

      <View style={styles.coverSpacer}>
        <View style={styles.coverTitleBlock}>
          <Text style={styles.coverKicker}>{kicker}</Text>
          <Text style={styles.coverTitle}>{titulo}</Text>

          <View style={styles.coverMetaGrid}>
            <View style={styles.coverMetaField}>
              <Text style={styles.coverMetaLabel}>CLIENTE</Text>
              <Text style={styles.coverMetaValue}>{empresa || '-'}</Text>
            </View>
            <View style={styles.coverMetaField}>
              <Text style={styles.coverMetaLabel}>OBRA</Text>
              <Text style={styles.coverMetaValue}>{obra || '-'}</Text>
            </View>
            <View style={styles.coverMetaField}>
              <Text style={styles.coverMetaLabel}>PERÍODO</Text>
              <Text style={styles.coverMetaValue}>{periodo}</Text>
            </View>
            <View style={styles.coverMetaField}>
              <Text style={styles.coverMetaLabel}>DATA DE EMISSÃO</Text>
              <Text style={styles.coverMetaValue}>{formatDateBrPdf(new Date())}</Text>
            </View>
          </View>
        </View>
      </View>

      <View style={styles.coverFooter}>
        <Text>concreto.tec.br</Text>
        <Text>Documento gerado eletronicamente</Text>
      </View>
    </Page>
  );
}

// Cabeçalho das páginas internas (repetido em todas as páginas da seção).
export function MedicaoCabecalho({ titulo, logoCliente, empresa, obra, periodo }) {
  return (
    <View style={styles.headerBox} fixed>
      <View style={styles.headerTop}>
        <View style={styles.headerTitleBlock}>
          <Text style={styles.headerTitle}>{titulo}</Text>
        </View>
        <View style={styles.headerLogoBlock}>
          <LogoRelatorio src={logoCliente} width={90} height={48} />
        </View>
      </View>
      <View style={styles.headerMeta}>
        <View style={styles.metaCell}>
          <Text style={styles.metaLabel}>Cliente</Text>
          <Text style={styles.metaValue}>{empresa || '-'}</Text>
        </View>
        <View style={styles.metaCell}>
          <Text style={styles.metaLabel}>Obra</Text>
          <Text style={styles.metaValue}>{obra || '-'}</Text>
        </View>
        <View style={styles.metaCellLast}>
          <Text style={styles.metaLabel}>Período</Text>
          <Text style={styles.metaValue}>{periodo}</Text>
        </View>
      </View>
    </View>
  );
}

// Assinaturas (apenas ao final do conteúdo) + numeração de páginas.
export function MedicaoRodape({ assinaturas }) {
  return (
    <>
      <View style={styles.signatureFooter} wrap={false}>
        <AssinaturasRodape assinaturas={assinaturas} />
      </View>
      <View style={styles.footer} fixed>
        <Text style={styles.footerText}>Emitido em: {formatDateBrPdf(new Date())}</Text>
        <Text style={styles.footerText} render={({ pageNumber, totalPages }) => `${pageNumber}/${totalPages}`} />
      </View>
    </>
  );
}

export function SummaryBox({ value, label }) {
  return (
    <View style={styles.summaryBox}>
      <Text style={styles.summaryValue}>{value}</Text>
      <Text style={styles.summaryLabel}>{label}</Text>
    </View>
  );
}
